/**
 * VISITOR TRAFFIC — the Overview headline chart, and the replacement for
 * /dashboard/home's area chart.
 *
 * ── IT READS THE HUB, NOT THE LEGACY ENDPOINT ──
 *
 * The home chart was fed by `/api/analytics/{daily,weekly,monthly}/:propertyId`,
 * which returns RAW LOG ROWS that the browser then bucketed by hand. That path
 * had four separate faults and every one of them changed the number on screen:
 *
 *   · it counted `numberOfGuests`, i.e. PEOPLE, under a tile labelled "Total
 *     entries";
 *   · it bucketed by `getUTCHours()` and then shifted the LABEL by the BROWSER's
 *     current offset — wrong zone, wrong on any DST day inside the window, and
 *     nonsense for a half-hour zone;
 *   · the monthly path re-filtered rows to the browser's CALENDAR month while
 *     the server had answered a desk-period month, silently dropping rows;
 *   · its "trend" was the second half of the window against the first, wearing
 *     the same ▲N% chip the analytics screen uses for a real period-over-period
 *     delta.
 *
 * This reads `measure=entries` split by the chosen granularity from the same
 * batch every other panel uses — counted as ROWS, in the BUILDING's own zone,
 * with the server's own comparison window. Overview and Trends therefore print
 * the same number, which they never did before.
 *
 * The four KPI figures the home screen had as tiles (total, peak, average,
 * active buckets) are captions and reference lines here rather than tiles that
 * disagree with the strip above them.
 */
import React, { useCallback, useMemo } from 'react';
import {
  Area,
  Bar,
  Brush,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AnalyticsBatchResult,
  isBatchFailure,
} from '../../../../../api/analytics-client';
import { dataViz, palette } from '../../../../../style-utils/theme';
import { ChartShell, ChartShellState } from '../charts/chart-shell';
import { ChartTable, describeSeries } from '../charts/chart-table-view';
import { DashLegend, hiddenKeys } from '../charts/chart-legend';
import { DashTooltip } from '../charts/chart-tooltip';
import {
  GRADIENT,
  ONE_BUCKET_NOTE,
  SYNC_ID,
  activeDot,
  compareSeries,
  cursor,
  fillUrl,
  firstPaintAnimation,
  format,
  grid,
  margins,
  oneBucket,
  oneBucketBar,
  oneBucketCompareBar,
  referenceLabel,
  referenceLine,
  soloPoint,
  xAxisTime,
  yAxisCount,
} from '../charts/chart-theme';
import { PanelToggle } from '../charts/chart.styled';
import { anomalyIndex, findAnomalies } from '../charts/anomaly';
import { MeterMeta, StatePill } from './panels.styled';
import { readSlot, slotState, substitutionCaveat } from './panel-utils';
import { DrillHandlers } from '../charts/use-chart-drill';
import { ZoomHandlers } from '../charts/use-chart-zoom';
import { useBrushAria } from '../charts/brush-aria';
import { useCompareMeaning } from '../charts/compare-label';

const PANEL_ID = 'traffic';

/** The captions the home screen carried as KPI tiles. Rendered under the chart,
 *  where they describe the series they came from. */
export const TrafficSummary: React.FC<{
  points: { label: string; value: number }[];
  granularity: string;
  anomalyCount: number;
}> = ({ points, granularity, anomalyCount }) => {
  const total = points.reduce((sum, p) => sum + p.value, 0);
  const active = points.filter(p => p.value > 0).length;
  const peak = points.reduce(
    (best, p) => (p.value > best.value ? p : best),
    points[0] || { label: '—', value: 0 }
  );
  const noun = granularity === 'week' ? 'week' : granularity === 'month' ? 'month' : 'day';

  if (!points.length) return null;

  return (
    <MeterMeta data-testid="traffic-summary">
      <span>{total.toLocaleString()} entries</span>
      <span>
        peak {peak.value.toLocaleString()} on {peak.label}
      </span>
      <span>
        {active} of {points.length} {noun}s had activity
      </span>
      {anomalyCount ? (
        <StatePill tone="warning">
          {anomalyCount} {anomalyCount === 1 ? 'bucket is' : 'buckets are'} well off the
          recent baseline
        </StatePill>
      ) : null}
    </MeterMeta>
  );
};


export interface TrafficPanelProps {
  state: ChartShellState;
  result?: AnalyticsBatchResult;
  granularity: string;
  comparing: boolean;
  endsAt?: string | null;
  seriesOff: string[];
  onToggleSeries: (key: string) => void;
  drill: DrillHandlers;
  zoom: ZoomHandlers;
  showBrush: boolean;
  onExportPng?: (svg: SVGSVGElement | null) => void;
  onExportCsv?: () => void;
}

export const TrafficPanel: React.FC<TrafficPanelProps> = ({
  state,
  result,
  granularity,
  comparing,
  endsAt,
  seriesOff,
  onToggleSeries,
  drill,
  zoom,
  showBrush,
  onExportPng,
  onExportCsv,
}) => {
  const extra: string[] = [];

  /* A ONE-BUCKET PERIOD IS DRAWN AS A BAR — see the one-bucket section of
     chart-theme. Read off the RESPONSE, not off `points`, because the caveat
     list is built before the points are. */
  const solo = !!result && !isBatchFailure(result) && oneBucket(result.items.length);

  if (solo) extra.push(ONE_BUCKET_NOTE);
  if (endsAt) {
    extra.push(
      `The last bucket on this chart is still in progress — the period ends at ${endsAt}, ` +
        'not at midnight — so its final point covers part of a bucket and the drop into ' +
        'it is the clock rather than a fall in activity.'
    );
  }

  const compareMeaning = useCompareMeaning();

  const slot = readSlot(result, extra);
  const data = slot.ok;

  const points = useMemo(
    () =>
      (data?.items || []).map(item => ({
        key: item.key,
        label: format.dayKey(item.key),
        value: format.numberOf(item.value) ?? 0,
        compare: item.compareValue,
      })),
    [data]
  );

  const total = points.reduce((sum, p) => sum + p.value, 0);
  /* THE AVERAGE OF ONE BUCKET IS THAT BUCKET, and drawn it lands exactly on the
     reading — a labelled line sitting on the only bar. Two buckets is the least
     that makes an average a second fact. */
  const mean = points.length > 1 ? total / points.length : 0;

  const anomalies = useMemo(
    () =>
      findAnomalies(
        points.map(p => ({ label: p.label, value: p.value })),
        { ignoreLast: Boolean(endsAt) }
      ),
    [points, endsAt]
  );

  const anomalyAt = useMemo(() => anomalyIndex(anomalies), [anomalies]);

  /* THE BRUSH IS A SLIDER AND ANNOUNCED ITSELF AS "undefined". See
     `charts/brush-aria.ts` — the library composes the handle's name from a
     `.name` field these rows do not have, and puts an SVG x-coordinate in
     `aria-valuenow`. This supplies the name, the range and the date. */
  const brushLabels = useMemo(() => points.map(p => p.label), [points]);
  const onBrushRange = useCallback(
    (startIndex: number, endIndex: number) => {
      const from = points[startIndex]?.key;
      const to = points[endIndex]?.key;
      if (from && to) zoom.onBrush(from, to);
    },
    /* `zoom` itself is rebuilt every render; `zoom.onBrush` is not. Depending on
       the object would rebind the brush's key listener on every keystroke
       anywhere on the screen. */
    [points, zoom.onBrush]
  );
  const brush = useBrushAria({
    panelId: PANEL_ID,
    labels: brushLabels,
    onRange: onBrushRange,
  });

  const hidden = hiddenKeys(seriesOff, PANEL_ID);
  const hasCompare = points.some(p => p.compare != null);

  const bucketNoun = granularity === 'week' ? 'week' : granularity === 'month' ? 'month' : 'day';

  return (
    <ChartShell
      id={PANEL_ID}
      title="Visitor traffic"
      wide
      aspect={2.8}
      minHeight={280}
      state={slotState(state, slot)}
      notes={slot.notes}
      caveat={`Log entries per ${bucketNoun}, counted as records in each building's own timezone. Drag across the chart to zoom, or use the handles below it to narrow the whole screen.`}
      extraCaveat={substitutionCaveat(data)}
      emptyReason={
        slot.empty
          ? {
              title: 'No visitor activity yet',
              text: 'Once guests are logged for this property, their traffic will appear here for the selected period. A quiet log and a quiet building are different things.',
            }
          : null
      }
      summary={describeSeries('Visitor traffic', points, ['entry', 'entries'])}
      onExportPng={onExportPng}
      onExportCsv={onExportCsv}
      /* THE FOUR FIGURES THE HOME SCREEN CARRIED AS TILES. They are properties of
         THIS series, so they sit under it rather than competing with the sticky
         strip at the top of the screen — which is where they used to disagree
         with it, because they were counted a different way. */
      footer={
        <TrafficSummary
          points={points}
          granularity={granularity}
          anomalyCount={anomalies.length}
        />
      }
      controls={
        <>
          {/* THE WAY BACK. The hook has always exposed `zoomed` and `reset` and
              nothing rendered them, so a drag-to-zoom was a one-way trip: the
              range became custom and the only exit was to guess a preset from
              the date menu. `reset` restores the preset that was actually on
              before the zoom, which is why this is not the same as picking one. */}
          {zoom.zoomed ? (
            <PanelToggle
              type="button"
              data-testid="reset-zoom"
              onClick={zoom.reset}
            >
              Reset zoom
            </PanelToggle>
          ) : null}
          <DashLegend
            panelId={PANEL_ID}
            entries={[
              { key: 'value', label: 'Entries', tone: dataViz.series[0] },
              ...(hasCompare
                ? [
                    {
                      key: 'compare',
                      label: compareMeaning.label,
                      tone: dataViz.axis,
                      dashed: true,
                    },
                  ]
                : []),
            ]}
            seriesOff={seriesOff}
            onToggle={onToggleSeries}
          />
        </>
      }
      table={
        data ? (
          <ChartTable
            dimensionLabel={format.humanise(bucketNoun)}
            valueLabel="Log entries"
            showCompare={comparing}
            rows={points.map(p => ({
              key: p.key,
              label: p.label,
              value: p.value.toLocaleString(),
              compareValue: p.compare == null ? '—' : p.compare.toLocaleString(),
              delta: p.compare == null ? '—' : format.delta(p.value - p.compare),
            }))}
          />
        ) : undefined
      }
    >
      <ComposedChart accessibilityLayer
        data={points}
        margin={showBrush ? margins.withBrush : margins.default}
        /* One category owns the whole plot width, so a group of two bars would
           be spread from edge to edge. Armed only in the one-bucket case — this
           panel draws no bars otherwise. */
        barCategoryGap={solo ? '38%' : undefined}
        barGap={solo ? 10 : undefined}
        syncId={SYNC_ID}
        onMouseDown={zoom.onMouseDown}
        onMouseMove={zoom.onMouseMove}
        onMouseUp={zoom.onMouseUp}
      >
        <CartesianGrid {...grid} />
        <XAxis dataKey="label" {...xAxisTime} />
        <YAxis {...yAxisCount} />
        <Tooltip
          content={
            <DashTooltip
              series={[
                {
                  key: 'value',
                  label: 'entries',
                  tone: dataViz.series[0],
                  noun: ['entry', 'entries'],
                },
                {
                  key: 'compare',
                  label: compareMeaning.inline,
                  tone: dataViz.axis,
                  dashed: true,
                  isCompare: true,
                },
              ]}
              hidden={hidden}
              total={total}
              note={row => {
                const anomaly = anomalyAt.get(String(row.label));

                return anomaly ? (
                  <>
                    <strong>Unusual:</strong> {anomaly.reason}
                  </>
                ) : null;
              }}
            />
          }
          cursor={cursor.line}
        />

        {/* THE AVERAGE AND THE PEAK, as lines rather than as tiles that disagree
            with the strip above. Both were KPI tiles on the home screen and both
            are properties of THIS series, which is where they belong. */}
        {mean > 0 ? (
          <ReferenceLine
            y={Math.round(mean * 10) / 10}
            {...referenceLine}
            label={{
              ...referenceLabel,
              value: `average ${
                mean >= 10
                  ? Math.round(mean).toLocaleString()
                  : (Math.round(mean * 10) / 10).toString()
              } per ${bucketNoun}`,
              position: 'insideTopRight',
            }}
          />
        ) : null}

        {zoom.selection ? (
          <ReferenceArea
            x1={zoom.selection.from}
            x2={zoom.selection.to}
            fill={palette.iris}
            fillOpacity={0.12}
            stroke={palette.iris}
          />
        ) : null}

        {hasCompare && !hidden.includes('compare') ? (
          solo ? (
            <Bar
              dataKey="compare"
              name={compareMeaning.label}
              {...oneBucketCompareBar}
            />
          ) : (
            <Line
              dataKey="compare"
              name={compareMeaning.label}
              {...compareSeries}
              {...soloPoint(points.length, dataViz.axis)}
            />
          )
        ) : null}

        {/* ONE BUCKET IS A QUANTITY, NOT A SHAPE. An area under a single point
            has zero area and recharts draws nothing for it; the reading becomes
            a bar rather than a bigger and bigger dot. See chart-theme. */}
        {!hidden.includes('value') ? (
          solo ? (
            <Bar
              dataKey="value"
              name="Entries"
              fill={dataViz.series[0]}
              {...oneBucketBar}
              onClick={drill.onMark}
              cursor="pointer"
            />
          ) : (
            <Area
              type="monotone"
              dataKey="value"
              name="Entries"
              stroke={dataViz.series[0]}
              strokeWidth={3}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill={fillUrl(GRADIENT.area)}
              fillOpacity={1}
              {...soloPoint(points.length, dataViz.series[0])}
              activeDot={{
                ...activeDot(dataViz.series[0]),
                r: 5,
                onClick: drill.onMark,
                cursor: 'pointer',
              }}
              {...firstPaintAnimation(PANEL_ID)}
            />
          )
        ) : null}

        {anomalies.map(anomaly => (
          <ReferenceDot
            key={anomaly.label}
            x={anomaly.label}
            y={anomaly.value}
            r={7}
            fill="none"
            stroke={palette.warning}
            strokeWidth={2}
            isFront
            data-testid="anomaly-marker"
          />
        ))}

        {showBrush && points.length > 6 ? (
          <Brush
            dataKey="label"
            height={22}
            travellerWidth={12}
            stroke={palette.lineStrong}
            fill={palette.surfaceMuted}
            // The generic name both handles share until the effect writes the
            // per-handle ones. Without it recharts composes the name from a
            // field these rows do not have and both handles announce
            // "Min value: undefined, Max value: undefined".
            ariaLabel={brush.ariaLabel}
            onChange={brush.onChange}
          />
        ) : null}
      </ComposedChart>
    </ChartShell>
  );
};
